import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export function useFollows(profileIdOrSlug: string) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const { user } = useAuth();

  // First, resolve the profile ID if we're given a slug
  useEffect(() => {
    const resolveProfileId = async () => {
      try {
        // Check if the input is a UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(profileIdOrSlug)) {
          setProfileId(profileIdOrSlug);
          return;
        }

        // Look up the profile ID from the slug
        const { data: resolvedId, error: lookupError } = await supabase
          .rpc('find_profile_by_slug', {
            search_slug: profileIdOrSlug
          });

        if (lookupError) throw lookupError;
        setProfileId(resolvedId);
      } catch (error) {
        console.error('Error resolving profile ID:', error);
        setProfileId(null);
      }
    };

    resolveProfileId();
  }, [profileIdOrSlug]);

  // Then fetch follow data once we have the profile ID
  useEffect(() => {
    if (!profileId) return;
    
    const fetchFollowCounts = async () => {
      try {
        // Get followers count
        const { data: followers } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('following_id', profileId);

        // Get following count
        const { data: following } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', profileId);

        setFollowersCount(followers?.length || 0);
        setFollowingCount(following?.length || 0);
      } catch (error) {
        console.error('Error fetching follow counts:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkIfFollowing = async () => {
      if (!user || !profileId) return;

      try {
        const { data } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_id', user.id)
          .eq('following_id', profileId)
          .maybeSingle();

        setIsFollowing(!!data);
      } catch (error) {
        console.error('Error checking follow status:', error);
      }
    };

    fetchFollowCounts();
    if (user) {
      checkIfFollowing();
    }
  }, [profileId, user]);

  const toggleFollow = async () => {
    if (!user || !profileId) return;

    try {
      if (isFollowing) {
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', profileId);
      } else {
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: profileId
          });
      }

      setIsFollowing(!isFollowing);
      await fetchFollowCounts();
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const fetchFollowCounts = async () => {
    if (!profileId) return;
    
    try {
      const { data: followers } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', profileId);

      const { data: following } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', profileId);

      setFollowersCount(followers?.length || 0);
      setFollowingCount(following?.length || 0);
    } catch (error) {
      console.error('Error fetching follow counts:', error);
    }
  };

  return {
    isFollowing,
    followersCount,
    followingCount,
    loading: loading || !profileId,
    toggleFollow
  };
}